// statements.js — Income & Expenses > Statements (Phase B, 2026-10-06)
//
// Four cards, one per credit card, resting on one line each; the detail opens on click.
// Contract: mockups/SPEKTRX_Statements_Mockup_v0.3.html (zen skin, approved 2026-10-03) and
// specs/SPEKTRX_Statements_Spec_2026-10-03.md.
//
// VIEWER architecture, same as tax.js: the statement engine on the owner's machine
// (statements/engine/stmtengine.py) parses the bank's PDFs, reconciles each statement to the
// bank's declared total and upserts a ~4 KB summary per statement into the Supabase table
// `statement_snapshots`. This module reads that table on boot, caches it in its own IDB key
// (`statement_snapshots`, never S, never the cloud blob) and paints. It writes nothing back.
//
// What crosses to the rest of the app is four facts per card through statement-bridge.js,
// for `cashflow.statementMismatch`: the Monthly row of the DUE month must say what the bank
// said. The first live case was October's Bonus row carrying September's number.

import { esc } from '../core/utils.js';
import { idbSaveStatements, idbLoadStatements } from '../core/persistence.js';
import { SB_REST, _sbHeaders, _sbUser } from '../core/auth.js';
import { S } from '../core/state.js';
import { publishStatements } from '../core/statement-bridge.js';
import { markDirty, renderOnly } from '../app/render-loop.js';

// The owner's four cards and their cut windows (day of month). Order is display order.
export const CARDS = [
  { id: 'axess', name: 'Axess', cut: [22, 24] },
  { id: 'bonus', name: 'Bonus', cut: [1, 3] },
  { id: 'pgs', name: 'Pgs', cut: [1, 3] },
  { id: 'maximum', name: 'Maximum', cut: [5, 7] },
];

// Engine categories (statements/rules/merchants.json) → labels. Every engine category must
// have a label here; the test pins the list against the rules file.
export const CATEGORY_LABEL = {
  dining: 'Food & dining',
  grocery: 'Grocery',
  clothing: 'Clothing',
  home: 'Home',
  fuel_car: 'Fuel & car',
  transport_parking: 'Transport & parking',
  education: 'Education',
  health: 'Health',
  insurance_pension: 'Insurance & pension',
  digital_subscription: 'Subscriptions',
  travel: 'Travel',
  gifts_leisure: 'Gifts & leisure',
  other: 'Other',
  unclassified: 'Unclassified',
};

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

let _rows = {};          // `${card}|${cut}` → snapshot data (engine schema v1)
let _importedAt = {};    // same key → updated_at from the cloud row
let _loaded = false;     // the IDB key has been read (null-is-not-empty gate for the bridge)
let _open = null;        // card id whose detail is open

// ── pure helpers (exported for the tests) ──────────────────────────────────────────────────

/** `YYYY-MM` of a Date, local time. */
export function ym(d) {
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
}

/** The newest statement per card, by cut date. `{ axess: data, ... }`; a card with none is absent. */
export function latestPerCard(rows) {
  const out = {};
  Object.values(rows || {}).forEach(r => {
    if (!r || !r.card || !r.cut) return;
    if (!out[r.card] || String(r.cut) > String(out[r.card].cut)) out[r.card] = r;
  });
  return out;
}

/** The four facts per card that cross the bridge, newest statement only. */
export function statementFacts(rows) {
  const latest = latestPerCard(rows);
  return CARDS.filter(c => latest[c.id]).map(c => {
    const r = latest[c.id];
    const h = r.header || {};
    return { card: c.id, cut: r.cut, due_month: r.due_month, total_due: Number(h.total_due), generated: r.generated || null, status: r.status || null };
  });
}

/**
 * The Monthly row for a card in its due month, and whether it matches the bank.
 * ⚠ Duplicates the rule in integrity.js `cashflow.statementMismatch` knowingly: that module may
 * not import. The two copies are pinned against each other by a test.
 */
export function monthlyRowFor(card, dueMonth, cashflow) {
  const name = (CARDS.find(c => c.id === card) || { name: card }).name;
  const mo = cashflow && cashflow[dueMonth];
  if (!mo) return { state: 'no-month', row: null, diff: null };
  const row = (mo.items || []).find(it => it && String(it.name || '').trim().toLowerCase() === name.toLowerCase());
  if (!row) return { state: 'no-row', row: null, diff: null };
  // a row that exists but was never typed (amount null) is "not typed", not "₺0"
  if (row.amount == null || row.amount === '') return { state: 'no-amount', row, diff: null };
  return { state: 'found', row, diff: Number(row.amount), closed: !!mo.closed };
}

/**
 * States: needs_review (engine could not reconcile the statement) · no-total (statement total
 * not read) · no-month · no-row · no-amount · match (within ₺1) · mismatch.
 */
export function reconState(card, snap, cashflow) {
  const h = (snap && snap.header) || {};
  if (snap && snap.status && snap.status !== 'ok') return { state: 'needs_review', row: null, diff: null };
  if (!Number.isFinite(Number(h.total_due))) return { state: 'no-total', row: null, diff: null };
  const m = monthlyRowFor(card, snap && snap.due_month, cashflow);
  if (m.state !== 'found') return m;
  const diff = Number(m.row.amount) - Number(h.total_due);
  return { state: Math.abs(diff) <= 1 ? 'match' : 'mismatch', row: m.row, diff, closed: m.closed };
}

/** The next cut window from `today`: `{ card, name, from, to, month, year }` (first card to cut wins ties by display order). */
export function nextWatch(today) {
  const d = today instanceof Date ? today : new Date(today);
  const day = d.getDate();
  let best = null;
  CARDS.forEach(c => {
    // a window still open this month counts; one that has closed moves to next month
    let y = d.getFullYear(), m = d.getMonth();
    if (day > c.cut[1]) { m += 1; if (m > 11) { m = 0; y += 1; } }
    const key = y * 100 + m, start = c.cut[0];
    if (!best || key < best.key || (key === best.key && start < best.from)) best = { key, card: c.id, name: c.name, from: c.cut[0], to: c.cut[1], month: m, year: y };
  });
  return best;
}

/** Category bar segments: top five by amount, the rest folded into "Other", unclassified last and hatched. */
export function barSegments(byCategory) {
  const entries = Object.entries(byCategory || {}).filter(([, v]) => Number(v) > 0);
  const unclassified = entries.filter(([k]) => k === 'unclassified');
  const named = entries.filter(([k]) => k !== 'unclassified').sort((a, b) => b[1] - a[1]);
  const top = named.slice(0, 5);
  const restSum = named.slice(5).reduce((s, [, v]) => s + v, 0);
  const segs = top.map(([k, v], i) => ({ key: k, label: CATEGORY_LABEL[k] || k, value: v, cls: 'c' + (i + 1) }));
  if (restSum > 0) segs.push({ key: 'rest', label: 'Everything else', value: restSum, cls: 'c6' });
  unclassified.forEach(([k, v]) => segs.push({ key: k, label: CATEGORY_LABEL[k], value: v, cls: 'c0' }));
  return segs;
}

const dMon = iso => {
  if (!iso || !/^\d{4}-\d{2}-\d{2}/.test(iso)) return '—';
  return String(Number(iso.slice(8, 10))) + ' ' + MON[Number(iso.slice(5, 7)) - 1];
};
const fT0 = n => '₺' + Math.round(Number(n || 0)).toLocaleString('en-US');
const fT2 = n => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const monthName = k => (/^\d{4}-\d{2}$/.test(String(k || ''))) ? MONTH_FULL[Number(k.slice(5, 7)) - 1] : String(k || '?');
const monthShort = k => (/^\d{4}-\d{2}$/.test(String(k || ''))) ? MON[Number(k.slice(5, 7)) - 1] : String(k || '?');

// ── lifecycle ──────────────────────────────────────────────────────────────────────────────

export async function initStatements() {
  const stored = await idbLoadStatements();
  _loaded = true;
  if (stored && stored.rows && typeof stored.rows === 'object') {
    _rows = stored.rows;
    _importedAt = stored.imported_at || {};
  }
  _publish();
  renderStatements();
  _cloudFetch(0);
}

async function _persist() {
  await idbSaveStatements({ rows: _rows, imported_at: _importedAt });
}

let _published = '\u0000';
function _publish() {
  // `_loaded`, not "are there any rows": an install with no engine has an EMPTY set, not an
  // unread one (tax.js learned this on 2026-08-10, at the cost of a bell slot).
  const next = _loaded ? statementFacts(_rows) : null;
  const sig = JSON.stringify(next);
  if (sig === _published) return;
  _published = sig;
  publishStatements(next);
  markDirty('dash', 'integrity');
  try { renderOnly('dash', 'integrity'); } catch (_e) { /* pre-DOM boot: the flags stand */ }
}

async function _cloudFetch(attempt) {
  const MAX_TRIES = 8;
  try {
    if (!_sbUser || !_sbUser.id) throw new Error('auth-not-ready');
    const headers = await _sbHeaders();
    if (!headers) throw new Error('no-token');
    const resp = await fetch(SB_REST + '/statement_snapshots?user_id=eq.' + _sbUser.id + '&select=card,cut,data,updated_at', { headers });
    if (!resp.ok) {
      if (resp.status === 404) return;      // table not created yet: the row says so
      throw new Error('http-' + resp.status);
    }
    const rows = await resp.json();
    let changed = false;
    (rows || []).forEach(row => {
      const d = row && row.data;
      if (!d || d.schema_version !== 1 || !d.card || !d.cut) return;
      const key = d.card + '|' + d.cut;
      const local = _rows[key];
      if (!local || String(d.generated || '') >= String(local.generated || '')) {
        _rows[key] = d;
        _importedAt[key] = row.updated_at || new Date().toISOString();
        changed = true;
      }
    });
    // The response is the user's full set: a row the cloud no longer has (an engine re-run that
    // corrected a misread cut date) must not live on in the cache as a phantom "latest".
    Object.keys(_rows).forEach(k => {
      if (!(rows || []).some(row => row && row.data && (row.data.card + '|' + row.data.cut) === k)) { delete _rows[k]; delete _importedAt[k]; changed = true; }
    });
    if (changed) {
      await _persist();
      _publish();
      renderStatements();
    }
  } catch (_e) {
    if (attempt < MAX_TRIES) setTimeout(() => _cloudFetch(attempt + 1), 2500);
  }
}

/** Click on a card: opens its detail, or closes it when already open. */
export function stmOpen(card) {
  _open = _open === card ? null : card;
  renderStatements();
}

// ── render ─────────────────────────────────────────────────────────────────────────────────

/** `now` is injectable so the tests are not date-bombed; the app passes nothing. */
export function renderStatements(now = new Date()) {
  const body = document.getElementById('stmBody');
  const sum = document.getElementById('stmSum');
  if (!body) return;
  const latest = latestPerCard(_rows);
  const thisMonth = ym(now);
  const onFile = Object.keys(_rows).length;
  const cashflow = S.cashflow || {};

  // row summary: cards · statements · installments next month · rows that are off
  let nextLoad = 0, mismatches = 0;
  CARDS.forEach(c => {
    const r = latest[c.id]; if (!r) return;
    const load = (r.summary && r.summary.installment_load) || {};
    const nm = Object.keys(load).sort().find(k => k > r.due_month);
    if (nm) nextLoad += Number(load[nm] || 0);
    const rs = reconState(c.id, r, cashflow);
    if (rs.state === 'mismatch' || rs.state === 'no-row' || rs.state === 'no-amount' || rs.state === 'needs_review' || rs.state === 'no-total') mismatches += 1;
  });
  if (sum) sum.textContent = !onFile
    ? 'no statements on file'
    : CARDS.length + ' cards · ' + onFile + ' statement' + (onFile === 1 ? '' : 's') + ' on file · ' + fT0(nextLoad) + ' of installments next'
      + (mismatches ? ' · ' + mismatches + ' Monthly row' + (mismatches === 1 ? '' : 's') + ' off' : '');

  const cards = CARDS.map(c => _cardTile(c, latest[c.id], thisMonth, cashflow)).join('');
  const detail = _open ? _detail(_open, latest[_open], cashflow) : '';
  body.innerHTML = '<div class="stm-grid">' + cards + '</div>' + detail + _engineLine(now);
}

function _cardTile(c, r, thisMonth, cashflow) {
  const win = 'cut ' + String(c.cut[0]).padStart(2, '0') + '–' + String(c.cut[1]).padStart(2, '0');
  const on = _open === c.id ? ' on' : '';
  const head = '<div class="stm-ch"><span class="stm-cn">' + esc(c.name) + '</span><span class="stm-cw">' + win + '</span></div>';
  if (!r) {
    return '<div class="stm-card wait' + on + '" data-action="stmOpen" data-args=\'["' + c.id + '"]\'>' + head +
      '<div class="stm-cv">—</div><div class="stm-cd">no statement on file</div>' +
      '<div class="stm-bar"><span class="c0" style="flex:1"></span></div>' +
      '<div class="stm-cf">drop the PDF in statements/' + c.id + '/</div></div>';
  }
  const h = r.header || {}, s = r.summary || {};
  const rs = reconState(c.id, r, cashflow);
  const stale = r.due_month < thisMonth;
  if (stale) {
    const m = monthlyRowFor(c.id, thisMonth, cashflow);
    const rowTxt = m.state === 'found' ? 'Monthly row ' + fT0(m.row.amount) + ' · not yet decomposed' : m.state === 'no-amount' ? 'Monthly row not typed yet' : 'Monthly row blank';
    return '<div class="stm-card wait' + on + '" data-action="stmOpen" data-args=\'["' + c.id + '"]\'>' + head +
      '<div class="stm-cv">—</div><div class="stm-cd">waiting for the ' + esc(monthName(thisMonth)) + ' statement</div>' +
      '<div class="stm-bar"><span class="c0" style="flex:1"></span></div>' +
      '<div class="stm-cf">' + esc(rowTxt) + ' · last cut ' + dMon(r.cut) + ' ' + fT0(h.total_due) + '</div></div>';
  }
  const MARK = {
    needs_review: '<span class="stm-bad">needs review</span>',
    'no-total': '<span class="stm-bad">total not read</span>',
    match: '<span class="stm-ok">✓</span>',
    mismatch: '<span class="stm-bad">✗ row ' + (rs.row ? fT0(rs.row.amount) : '') + '</span>',
    'no-row': '<span class="stm-bad">no Monthly row</span>',
    'no-amount': '<span class="stm-bad">Monthly row not typed</span>',
    'no-month': '<span class="stm-dim">no Monthly month</span>',
  };
  const mark = MARK[rs.state] || '';
  const segs = barSegments(s.by_category);
  const bar = segs.length ? segs.map(g => '<span class="' + g.cls + '" style="flex:' + Math.round(g.value) + '" title="' + esc(g.label + ' ' + fT0(g.value)) + '"></span>').join('') : '<span class="c0" style="flex:1"></span>';
  const inst = (s.installments || []).filter(i => i.left > 0);
  const load = s.installment_load || {};
  const nm = Object.keys(load).sort().find(k => k > r.due_month);
  const rec = (s.recurring || []).filter(x => x.every === 'monthly');
  const recN = new Set(rec.map(x => x.merchant)).size;
  const foot = inst.length + ' installment' + (inst.length === 1 ? '' : 's') + (nm ? ' · ' + fT0(load[nm]) + ' next' : '') + ' · ' + recN + ' recurring';
  return '<div class="stm-card' + on + '" data-action="stmOpen" data-args=\'["' + c.id + '"]\'>' + head +
    '<div class="stm-cv">' + fT0(h.total_due) + '</div>' +
    '<div class="stm-cd">' + dMon(r.cut) + ' · due ' + dMon(r.due) + ' ' + mark + '</div>' +
    '<div class="stm-bar">' + bar + '</div>' +
    '<div class="stm-cf">' + esc(foot) + '</div></div>';
}

function _detail(card, r, cashflow) {
  const c = CARDS.find(x => x.id === card) || { name: card, id: card };
  if (!r) {
    return '<div class="stm-detail stm-empty"><div class="neo-empty">No statement yet. Drop the PDF in <code>statements/' + esc(c.id) + '/</code>; the engine watches the folder daily and on each cut window.</div></div>';
  }
  const h = r.header || {}, s = r.summary || {};
  const rs = reconState(card, r, cashflow);
  const paidInFull = Number(h.prior_balance || 0) > 0 && Math.abs(Number(h.payments || 0) - Number(h.prior_balance || 0)) < 0.01;
  const prior = Number(h.prior_balance || 0) === 0 ? 'no balance carried' : paidInFull ? 'prior balance paid in full' : 'prior balance ' + fT0(h.prior_balance) + ' · paid ' + fT0(h.payments);
  const title = esc(c.name) + ' <span class="stm-dim">· cut ' + dMon(r.cut) + ' · due ' + dMon(r.due) + ' · ' + esc(String(s.lines || 0)) + ' lines · ' + esc(prior) + (r.source === 'ocr' ? ' · OCR' : '') + '</span>';
  const mo = esc(monthShort(r.due_month));
  const RECON = {
    needs_review: '<span class="stm-bad">needs review</span> parsed ' + fT0(s.spend) + ' vs declared ' + fT0(s.declared_spend) + ' (delta ' + fT2(s.delta) + ')',
    'no-total': '<span class="stm-bad">statement total not read</span> · parsed ' + fT0(s.spend),
    match: '<span class="stm-ok">✓</span> matches Monthly · ' + mo + ' · ' + esc(c.name) + ' ' + (rs.row ? fT0(rs.row.amount) : ''),
    mismatch: '<span class="stm-bad">✗</span> Monthly · ' + mo + ' · ' + esc(c.name) + ' row ' + (rs.row ? fT0(rs.row.amount) : '') + ' vs statement ' + fT0(h.total_due),
    'no-row': '<span class="stm-bad">✗</span> no ' + esc(c.name) + ' row in Monthly · ' + mo,
    'no-amount': '<span class="stm-bad">✗</span> Monthly · ' + mo + ' · ' + esc(c.name) + ' row not typed yet · statement ' + fT0(h.total_due),
    'no-month': '<span class="stm-dim">Monthly · ' + mo + ' not opened yet</span>',
  };
  const recon = RECON[rs.state] || '';

  const segs = barSegments(s.by_category);
  const legend = segs.map(g => '<span><i class="' + g.cls + '"></i>' + esc(g.label) + ' <b>' + fT0(g.value) + '</b></span>').join('');

  // recurring: one line per merchant, latest amount, every; variable merchants show the period sum
  const rec = {};
  (s.recurring || []).slice().sort((a, b) => String(a.date).localeCompare(String(b.date))).forEach(x => {
    const k = x.merchant;
    if (!rec[k]) rec[k] = { merchant: k, every: x.every, currency: x.currency, amount: 0, fx: 0, n: 0, variable: !!x.variable, cpm: Number(x.charges_per_month || 1), latest: [] };
    rec[k].n += 1; rec[k].latest.push(x);
  });
  const recLines = Object.values(rec).map(x => {
    const take = x.variable ? x.latest : x.latest.slice(-x.cpm);
    const amt = take.reduce((t, y) => t + Number(y.amount || 0), 0);
    const fx = take.reduce((t, y) => t + Number(y.amount_fx || 0), 0);
    const tag = x.every === 'annual' ? 'yearly' : x.variable ? 'monthly · usage' : x.cpm > 1 ? 'monthly · ' + x.cpm + ' charges' : 'monthly';
    const ccy = x.currency && x.currency !== 'TRY' ? ' <i>' + esc(x.currency) + ' ' + fT2(fx) + '</i>' : '';
    return '<div class="stm-line"><span>' + esc(x.merchant) + '</span><b>' + fT0(amt) + '</b>' + ccy + '<em>' + tag + '</em></div>';
  }).join('') || '<div class="stm-note">none on this statement</div>';
  const recTotal = Number(s.recurring_monthly_try || 0);

  // installments: open plans with progress, then what is still to come by month
  // open plans only; the ones that ended on this statement are counted, not listed
  const allInst = s.installments || [];
  const inst = allInst.filter(i => i.left > 0).sort((a, b) => b.remaining - a.remaining);
  const ended = allInst.length - inst.length;
  const instRows = inst.map(i => {
    const pct = Math.round(100 * i.n / i.of);
    const ends = i.ends === 'done' ? 'done' : i.ends ? 'ends ' + monthShort(i.ends) : '';
    return '<tr><td>' + esc(i.merchant) + '</td><td class="tr">' + fT2(i.per) + '</td><td><span class="stm-prog"><i style="width:' + (Number.isFinite(pct) ? pct : 0) + '%"></i></span>' + esc(i.n + '/' + i.of) + '</td><td class="tr stm-dim">' + esc(ends) + '</td></tr>';
  }).join('');
  const load = s.installment_load || {};
  const fwd = Object.keys(load).sort().filter(k => k > r.due_month).slice(0, 4).map(k => '<span>' + esc(monthShort(k)) + ' <b>' + fT0(load[k]) + '</b></span>').join('');
  const endedNote = ended ? '<div class="stm-note">' + ended + ' plan' + (ended === 1 ? '' : 's') + ' paid off on this statement</div>' : '';
  const instBlock = inst.length
    ? '<table class="stm-tbl"><tbody>' + instRows + '</tbody></table>' + (fwd ? '<div class="stm-fwd"><span class="stm-dim">to come</span>' + fwd + '</div>' : '') + endedNote
    : '<div class="stm-note">no open installment plans</div>' + endedNote;

  // third column: what the engine could not place, else the reconciliation facts
  const uncl = Object.entries(s.unclassified || {}).sort((a, b) => b[1].amount - a[1].amount);
  const third = uncl.length
    ? '<div class="l">Unclassified · ' + uncl.length + ' merchant' + (uncl.length === 1 ? '' : 's') + '</div>' +
      uncl.map(([k, v]) => '<div class="stm-line"><span>' + esc(k) + (v.count > 1 ? ' (×' + esc(String(v.count)) + ')' : '') + '</span><b>' + fT0(v.amount) + '</b></div>').join('') +
      '<div class="stm-note">Add a rule in statements/rules/merchants.json; the engine re-runs on save.</div>'
    : '<div class="l">Reconciliation</div>' +
      '<div class="stm-line"><span>declared by the bank</span><b>' + fT2(s.declared_spend) + '</b></div>' +
      '<div class="stm-line"><span>parsed</span><b>' + fT2(s.spend) + '</b></div>' +
      '<div class="stm-line"><span>delta</span><b>' + fT2(s.delta) + '</b></div>' +
      '<div class="stm-line"><span>recurring, monthly</span><b>' + fT0(recTotal) + '</b></div>' +
      '<div class="stm-line"><span>source</span><b>' + esc(r.source === 'ocr' ? 'OCR, two passes' : 'text') + '</b></div>' +
      (s.suspicious_ocr && s.suspicious_ocr.length ? '<div class="stm-note">OCR lines not confirmed by both passes: ' + esc(s.suspicious_ocr.join(', ')) + '</div>' : '');

  // earlier statements of this card
  const earlier = Object.values(_rows).filter(x => x && x.card === card && x.cut !== r.cut).sort((a, b) => String(b.cut).localeCompare(String(a.cut)));
  const earlierBlock = earlier.length
    ? '<details class="jrn-more"><summary>Earlier statements · ' + earlier.length + ' ▾</summary>' +
      earlier.map(x => '<div class="stm-line"><span>cut ' + dMon(x.cut) + ' · due ' + esc(monthShort(x.due_month)) + '</span><b>' + fT0((x.header || {}).total_due) + '</b><em>' + esc(String((x.summary || {}).lines || 0)) + ' lines · ' + esc(x.status || '') + '</em></div>').join('') +
      '</details>'
    : '';

  return '<div class="stm-detail">' +
    '<div class="stm-sthd"><div class="stm-stt">' + title + '</div><div class="stm-recon">' + recon + '</div></div>' +
    '<div class="stm-legend">' + legend + '</div>' +
    '<div class="stm-3col">' +
      '<div><div class="l">Recurring · ' + fT0(recTotal) + ' / month</div>' + recLines + '</div>' +
      '<div><div class="l">Installments</div>' + instBlock + '</div>' +
      '<div>' + third + '</div>' +
    '</div>' +
    '<div class="stm-note">Lines stay on disk (statements/parsed/' + esc(card) + '-' + esc(r.cut) + '.json); the app carries the summary.</div>' +
    earlierBlock +
    '</div>';
}

function _engineLine(now) {
  const all = Object.values(_rows);
  const newest = all.reduce((m, r) => (r && r.generated && (!m || String(r.generated) > String(m))) ? r.generated : m, null);
  const rules = all.reduce((m, r) => Math.max(m, Number((r && r.rules) || 0)), 0);
  const nw = nextWatch(now);
  let ran = 'no run on record';
  if (newest) {
    // the agent runs nightly (com.spektrx.stmtengine, 21:00), so a week without a run is a dead agent
    const t = Date.parse(newest);
    const ageD = Number.isFinite(t) ? Math.floor((now.getTime() - t) / 86400000) : null;
    ran = 'last run ' + dMon(newest) + (ageD != null && ageD > 7 ? ' <span class="stm-bad">· stale, ' + ageD + ' days</span>' : '');
  }
  const watch = nw ? 'next watch ' + esc(nw.name) + ' ' + String(nw.from).padStart(2, '0') + '–' + String(nw.to).padStart(2, '0') + ' ' + MON[nw.month] : '';
  return '<div class="stm-eng">engine · ' + ran + (rules ? ' · ' + rules + ' merchant rules' : '') + ' · ' + watch + '</div>';
}

/** Test seam: load rows without IDB or the network. */
export function _setRowsForTest(rows, opts = {}) {
  _rows = rows || {};
  _loaded = true;
  _open = opts.open || null;
  _published = '\u0000';
  _publish();
}
